import { FC } from "react";

import { PageLink } from "./PageLink";

import { getPageType, tableDateFormat } from "components/materials/page/helpers";
import { displayOutcomeName } from "components/outcomes/helpers";
import { ActivityType, PageOutcomeType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { CommentIcon } from "mds/icons";
import { PageWithAssessmentsType } from "store/selectors";
import { NestedSectionOutcomeType } from "store/selectors/types";

type StudentScoreOverviewTableProps = {
  pagesWithAssessments: PageWithAssessmentsType[];
  activitiesById: Record<string, ActivityType>;
  sectionOutcomes: NestedSectionOutcomeType[];
  pageOutcomes: PageOutcomeType[];
};

export const StudentScoreOverviewTable: FC<StudentScoreOverviewTableProps> = ({
  pagesWithAssessments,
  activitiesById,
  sectionOutcomes,
  pageOutcomes,
}) => {
  if (pagesWithAssessments.length === 0) {
    return null;
  }

  return (
    <ScrollableTableContainer
      className="w-full overflow-hidden"
      title={t("assessment_dashboard.all.title")}
    >
      <Table>
        {/* Headers: Page / Page Type / Points / Outcomes / Submitted At */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="pl-4">{t("assessment_dashboard.all.page_topic_header_title")}</TH>

            <TH>{t("assessment_dashboard.all.page_header_title")}</TH>

            <TH>
              <Tooltip text={t("assessment_dashboard.all.page_type_header_tooltip")}>
                {t("assessment_dashboard.all.page_type_header_title")}
              </Tooltip>
            </TH>

            <TH>{t("assessment_dashboard.student.points_header_title")}</TH>

            <TH>{t("assessment_dashboard.all.outcomes_header_title")}</TH>

            <TH>{t("assessment_dashboard.student.submitted_header_title")}</TH>
          </TR>
        </THead>

        <tbody>
          {pagesWithAssessments.map((page) => {
            // TODO: The selector should allow for non-final submissions so that we can display comments here
            // There should never be more than one assessment for a page and user, but just
            // in case something fishy happens, we handle it gracefully here.
            const officialAssessment = page.assessments[0];
            const points = officialAssessment?.points;
            const submittedDate =
              officialAssessment?.submission &&
              tableDateFormat(officialAssessment?.submission?.created_at);

            const assessmentsWithComments = page.nonOfficialAssessments
              .concat(page.assessments)
              .filter((a) => a.comment?.trim());
            const pageSpecificOutcomes = pageOutcomes.filter((po) => po.page_id === page.id);

            return (
              <TR key={page.id}>
                <TD className="max-w-52 truncate py-2.5 pl-[17px]" title={page.topic?.title}>
                  {page.topic?.title || "–"}
                </TD>

                <TD title={page.title}>
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 max-w-40 truncate">
                      <PageLink
                        className="w-full truncate text-blue-shade-20 underline"
                        page={page}
                      />
                    </div>
                    {assessmentsWithComments.length > 0 &&
                      assessmentsWithComments.map((assessment) => (
                        <Tooltip
                          key={assessment.id}
                          text={
                            <div className="body-xs text-black-tint-40">{assessment.comment}</div>
                          }
                        >
                          <CommentIcon />
                        </Tooltip>
                      ))}
                  </div>
                </TD>

                <TD>{getPageType(page, activitiesById)}</TD>

                <TD>{typeof points === "number" ? `${points}/${page.max_points}` : "-"}</TD>

                <TD>
                  <Tooltip
                    text={pageSpecificOutcomes
                      .flatMap((po) => {
                        const sectionOutcome = sectionOutcomes.find(
                          (co) => co.id === po.section_outcome_id,
                        );
                        return sectionOutcome ? [displayOutcomeName(sectionOutcome.outcome)] : [];
                      })
                      .join(", ")}
                  >
                    {pageSpecificOutcomes.length || "0"}
                  </Tooltip>
                </TD>

                <TD>{submittedDate || "-"}</TD>
              </TR>
            );
          })}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
